<script setup lang="ts">
import { computed } from 'vue'
import UIAvatar from './UIAvatar.vue'
import MediaUrl from '@/components/common/MediaUrl.vue'
import type { MessageAttachment } from '@/utils/media'
import {PhCheck, PhChecks, PhPaperclip, PhClock, PhWarningCircle} from '@phosphor-icons/vue'

interface Props {
  text: string
  own: boolean
  status?: 'sending' | 'sent' | 'delivered' | 'read' | 'failed'
  avatar?: string
  avatarFallback?: string
  /** Blob previews or stored upload paths; private uploads resolve to a signed link. */
  attachments?: MessageAttachment[]
  timestamp?: string
  isFirstInGroup?: boolean
  isLastInGroup?: boolean
  showAvatar?: boolean
  showTail?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  own: false,
  status: 'sent',
  showAvatar: true,
  showTail: true,
})

const statusIcons = {
  sending: PhClock,
  sent: PhCheck,
  delivered: PhChecks,
  read: PhChecks,
  failed: PhWarningCircle,
}

const statusColors = {
  sending: 'text-ink-6',
  sent: 'text-ink-6',
  delivered: 'text-accent-cyan',
  read: 'text-accent-cyan',
  failed: 'text-accent-red',
}

const tailClasses = computed(() => [
  'absolute bottom-0 w-4 h-4',
  props.own ? 'right-[-4px]' : 'left-[-4px]',
])

const timeAgo = computed(() => {
  if (!props.timestamp) return ''
  const date = new Date(props.timestamp)
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
})

const StatusIcon = computed(() => statusIcons[props.status] || PhCheck)
</script>

<template>
  <div
    class="flex w-full gap-2 items-end"
    :class="props.own ? 'justify-end' : 'justify-start'"
    role="listitem"
  >
    <!-- Counterpart Avatar (only for received messages) -->
    <template v-if="!props.own">
      <UIAvatar
        v-if="props.showAvatar"
        :src="props.avatar"
        :fallback="props.avatarFallback"
        size="sm"
        class="shrink-0 mb-1"
      />
      <div v-else class="w-8 shrink-0" aria-hidden="true" />
    </template>

    <!-- Message Bubble Container -->
    <div
      class="flex flex-col max-w-[82%] sm:max-w-[70%]"
      :class="props.own ? 'items-end' : 'items-start'"
    >
      <div
        v-if="props.isFirstInGroup && !props.own && props.avatarFallback"
        class="text-xs text-ink-5 ml-1 mb-1 font-medium"
      >
        {{ props.avatarFallback }}
      </div>

      <div class="relative w-fit max-w-full" :class="{ 'group': props.showTail }">
        <div
          class="px-4 py-2.5 rounded-2xl animate-fade-in w-fit max-w-full wrap-break-word text-sm sm:text-base leading-relaxed"
          :class="props.own
            ? 'rounded-tr-sm bg-linear-to-br from-cyan-400 to-sky-500 text-on-accent font-medium shadow-[0_4px_15px_rgba(37,227,233,0.15)]'
            : 'rounded-tl-sm bg-surface text-ink-2 border border-line'"
        >
          <div v-if="props.attachments && props.attachments.length > 0" class="mb-2 space-y-2">
            <MediaUrl
              v-for="att in props.attachments"
              :key="att.url"
              v-slot="{ url }"
              :src="att.url"
            >
              <img
                v-if="att.type === 'image'"
                :src="url || undefined"
                :alt="att.name || 'Attachment'"
                class="block w-full max-h-48 min-h-16 rounded-xl object-cover"
                loading="lazy"
              />
              <a
                v-else
                :href="url || undefined"
                target="_blank"
                rel="noopener"
                class="flex items-center gap-3 p-3 rounded-xl bg-surface-3/50"
              >
                <PhPaperclip class="w-5 h-5 text-ink-5" />
                <span class="flex-1 min-w-0 text-sm font-medium truncate">{{ att.name || 'File' }}</span>
              </a>
            </MediaUrl>
          </div>

          <p class="whitespace-pre-wrap wrap-break-word leading-relaxed">{{ props.text }}</p>
        </div>

        <div
          v-if="props.showTail && props.isLastInGroup"
          :class="tailClasses"
          aria-hidden="true"
        >
          <div
            v-if="props.own"
            class="w-4 h-4 bg-cyan-400 border-r border-b border-cyan-400 rounded-tr-full -mr-px -mb-px"
          />
          <div
            v-else
            class="w-4 h-4 bg-surface border-l border-b border-line rounded-tl-full -ml-px -mb-px"
          />
        </div>

        <div class="flex items-center gap-1.5 mt-1 px-1" :class="props.own ? 'justify-end' : 'justify-start'">
          <span class="text-[10px] text-ink-6 tabular-nums">{{ timeAgo }}</span>
          <component
            v-if="props.own"
            :is="StatusIcon"
            class="w-3.5 h-3.5 shrink-0"
            :class="statusColors[props.status]"
            :aria-label="props.status"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}

@media (prefers-reduced-motion: reduce) {
  .animate-fade-in {
    animation: none;
  }
}
</style>